/**
 * Gestor de IPs de la ficha tarjetas.
 *
 * Sustituye al `ElementIpPanel` completo dentro de «Gestionar IPs». Mantiene lo
 * esencial (alta, marcar primaria y quitar) con el estilo y los tokens del tema,
 * sin los colores fijos del panel antiguo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const TIPOS = [
  { value: 'static', label: 'Estático' },
  { value: 'dhcp', label: 'DHCP' },
  { value: 'vip', label: 'VIP' },
  { value: 'carp', label: 'CARP' },
  { value: 'vrrp', label: 'VRRP' },
  { value: 'hsrp', label: 'HSRP' },
];

const MODOS = [
  { value: 'auto', label: 'Automática' },
  { value: 'manual', label: 'Manual' },
  { value: 'map', label: 'Mapa' },
  { value: 'existing', label: 'IP registrada' },
];

function ipOcupada(ip) {
  if (!ip) return false;
  if (ip.status && ip.status !== 'free') return true;
  return (ip.assignments || []).length > 0;
}

function etiquetaInterfaz(ref, nicMap) {
  if (!ref) return '—';
  if (ref === 'bmc') return 'BMC / iLO';
  if (ref === 'mgmt') return 'mgmt';
  if (ref.startsWith('team:')) return `team ${ref.slice(5)}`;
  if (ref.startsWith('nic-')) return nicMap[ref] || ref;
  return ref;
}

export default function ServerCardsIpManager({
  objectId,
  companyId,
  teams = [],
  onChanged,
  onCountChange,
}) {
  const [subnets, setSubnets] = useState([]);
  const [nicMap, setNicMap] = useState({});
  const [asignaciones, setAsignaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [buscandoLibre, setBuscandoLibre] = useState(false);
  const [form, setForm] = useState({
    subnet_id: '',
    ip: '',
    ip_id: '',
    interface_ref: '',
    assignment_type: 'static',
    is_primary: false,
    comments: '',
  });
  const [mode, setMode] = useState('auto');
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [publicIps, setPublicIps] = useState([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [onlyFree, setOnlyFree] = useState(true);
  const [nextIp, setNextIp] = useState('');
  const [nextIpLoading, setNextIpLoading] = useState(false);

  const cargar = useCallback(async () => {
    if (!objectId || !companyId) { setCargando(false); return; }
    setCargando(true);
    setError('');
    try {
      const [subRes, asgRes, compRes] = await Promise.all([
        api.getIpamSubnets(companyId).catch(() => ({ data: [] })),
        Promise.all([
          api.getIpamAssignments(companyId, { object_type: 'server', object_id: objectId }).catch(() => ({ data: [] })),
          api.getIpamAssignments(companyId, { object_type: 'server_team', object_id: objectId }).catch(() => ({ data: [] })),
          api.getIpamAssignments(companyId, { object_type: 'rack_element', object_id: objectId }).catch(() => ({ data: [] })),
        ]),
        api.getComponents(objectId, companyId).catch(() => ({ data: [] })),
      ]);
      setSubnets((subRes.data || []).filter((s) => s.status === 'active'));
      const vistos = new Set();
      const todas = asgRes
        .flatMap((r) => r.data || [])
        .filter((a) => {
          if (a.status !== 'active') return false;
          if (vistos.has(a.id)) return false;
          vistos.add(a.id);
          return true;
        });
      setAsignaciones(todas);
      onCountChange?.(todas.length);

      const map = {};
      (compRes.data || []).forEach((c) => {
        if (c.component_type === 'nic' || c.component_type === 'hba') {
          (c.ports || []).forEach((p) => {
            map[`nic-${p.id}`] = `${c.model || 'NIC'} · ${p.label || `P${p.port_number}`}`;
          });
        }
      });
      setNicMap(map);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las IPs');
    } finally {
      setCargando(false);
    }
  }, [objectId, companyId, onCountChange]);

  useEffect(() => { cargar(); }, [cargar]);

  // Mapa de IPs de la subred seleccionada.
  useEffect(() => {
    if (mode !== 'map' || !form.subnet_id || !companyId) {
      setMapData(null);
      return undefined;
    }
    let cancelado = false;
    setMapLoading(true);
    api.getIpamSubnetMap(form.subnet_id, companyId, 512)
      .then((res) => { if (!cancelado) setMapData(res.data || null); })
      .catch(() => { if (!cancelado) setMapData(null); })
      .finally(() => { if (!cancelado) setMapLoading(false); });
    return () => { cancelado = true; };
  }, [mode, form.subnet_id, companyId]);

  // IPs ya registradas en IPAM (modo «IP registrada»).
  useEffect(() => {
    if (mode !== 'existing' || !companyId) return undefined;
    let cancelado = false;
    setLoadingPublic(true);
    api.getIpamIPs(companyId, { scope: 'public', limit: 500 })
      .then((res) => { if (!cancelado) setPublicIps(res.data || []); })
      .catch(() => { if (!cancelado) setPublicIps([]); })
      .finally(() => { if (!cancelado) setLoadingPublic(false); });
    return () => { cancelado = true; };
  }, [mode, companyId]);

  // Próxima IP libre de la subred (modo automático).
  useEffect(() => {
    if (mode !== 'auto' || !form.subnet_id || !companyId) {
      setNextIp('');
      return undefined;
    }
    let cancelado = false;
    setNextIpLoading(true);
    api.getIpamSubnetNextIp(form.subnet_id, companyId, 1)
      .then((res) => { if (!cancelado) setNextIp((res?.data?.ips || [])[0] || ''); })
      .catch(() => { if (!cancelado) setNextIp(''); })
      .finally(() => { if (!cancelado) setNextIpLoading(false); });
    return () => { cancelado = true; };
  }, [mode, form.subnet_id, companyId]);

  const cambiarModo = (nuevo) => {
    setMode(nuevo);
    setForm((prev) => ({ ...prev, ip: '', ip_id: '' }));
    setError('');
    setAviso('');
  };

  const opcionesInterfaz = useMemo(() => {
    const opciones = [
      { value: '', label: '— sin interfaz —' },
      { value: 'bmc', label: 'BMC / iLO' },
      { value: 'mgmt', label: 'mgmt (gestión)' },
    ];
    (teams || []).forEach((t) => {
      const nombre = String(t?.name || '').trim();
      if (nombre) opciones.push({ value: `team:${nombre}`, label: `Team ${nombre}` });
    });
    Object.entries(nicMap).forEach(([value, label]) => opciones.push({ value, label }));
    return opciones;
  }, [teams, nicMap]);

  const pedirSiguienteLibre = async () => {
    if (!form.subnet_id) { setError('Selecciona primero una subred.'); return; }
    setBuscandoLibre(true);
    setError('');
    try {
      const res = await api.getIpamSubnetNextIp(form.subnet_id, companyId, 1);
      const ip = (res?.data?.ips || [])[0];
      if (!ip) setError('La subred no tiene IPs libres.');
      else setForm((prev) => ({ ...prev, ip }));
    } catch (e) {
      setError(e.message || 'No se pudo calcular la siguiente IP libre');
    } finally {
      setBuscandoLibre(false);
    }
  };

  const anadir = async (e) => {
    e.preventDefault();
    // El gestor vive dentro del <form> de la ficha: sin frenar la propagación,
    // dar de alta una IP dispararía también el guardado del servidor.
    e.stopPropagation();
    if (mode === 'existing') {
      if (!form.ip_id) { setError('Selecciona una IP registrada.'); return; }
    } else {
      if (!form.subnet_id) { setError('Selecciona una subred.'); return; }
      if (mode === 'manual' && !form.ip.trim()) { setError('Escribe una dirección IP.'); return; }
      if (mode === 'map' && !form.ip.trim()) { setError('Selecciona una IP del mapa.'); return; }
    }
    setGuardando(true);
    setError('');
    setAviso('');
    try {
      const payload = {
        object_type: 'server',
        object_id: objectId,
        interface_ref: form.interface_ref || null,
        is_primary: form.is_primary,
        assignment_type: form.assignment_type,
        comments: form.comments.trim() || null,
      };
      if (mode === 'existing') {
        payload.ip_id = Number(form.ip_id);
      } else {
        payload.subnet_id = Number(form.subnet_id);
        if (mode === 'manual' || mode === 'map') payload.ip = form.ip.trim();
      }
      await api.createIpamAssignment(payload, companyId);
      setAviso('IP asignada.');
      setForm((prev) => ({ ...prev, ip: '', ip_id: '', is_primary: false, comments: '' }));
      await cargar();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo asignar la IP');
    } finally {
      setGuardando(false);
    }
  };

  const marcarPrimaria = async (asignacion) => {
    setError('');
    try {
      await api.patchIpamAssignment(asignacion.id, { is_primary: true }, companyId);
      await cargar();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo marcar como primaria');
    }
  };

  const quitar = async (asignacion) => {
    if (!window.confirm(`¿Quitar la IP ${asignacion.ip} de este servidor?`)) return;
    setError('');
    try {
      await api.deleteIpamAssignment(asignacion.id, companyId);
      await cargar();
      onChanged?.();
    } catch (e) {
      setError(e.message || 'No se pudo quitar la IP');
    }
  };

  if (cargando) return <div className="sf2-empty">Cargando gestión de IPs…</div>;

  const sinSubredes = subnets.length === 0;
  const bloqueaAlta = sinSubredes && mode !== 'existing';
  const publicVisibles = onlyFree ? publicIps.filter((p) => !ipOcupada(p)) : publicIps;

  return (
    <div className="sf2-ip-manager">
      {sinSubredes && mode !== 'existing' && (
        <div className="sf2-alert" style={{ marginBottom: 0 }}>
          <span>No hay subredes IPAM activas. Crea bloques y subredes en IPAM → Subredes para asignar IPs.</span>
        </div>
      )}

      <div className="sf2-mode">
        {MODOS.map((m) => (
          <button
            key={m.value}
            type="button"
            className={`sf2-mode-btn${mode === m.value ? ' active' : ''}`}
            onClick={() => cambiarModo(m.value)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <form className="sf2-ip-form" onSubmit={anadir}>
        <div className="sf2-fields" style={{ '--n': 3 }}>
          {mode !== 'existing' && (
            <div className="sf2-field">
              <label>Subred</label>
              <select
                value={form.subnet_id}
                onChange={(e) => setForm((prev) => ({ ...prev, subnet_id: e.target.value }))}
                disabled={guardando}
              >
                <option value="">— Selecciona subred —</option>
                {subnets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.cidr}{s.name ? ` · ${s.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === 'existing' ? (
            <div className="sf2-field">
              <label>IP registrada</label>
              <select
                value={form.ip_id}
                onChange={(e) => setForm((prev) => ({ ...prev, ip_id: e.target.value }))}
                disabled={guardando || loadingPublic}
              >
                <option value="">— Selecciona IP —</option>
                {publicVisibles.map((p) => (
                  <option key={p.id} value={p.id} disabled={ipOcupada(p)}>
                    {p.ip}{p.subnet_cidr ? ` · ${p.subnet_cidr}` : ''}{p.dns_name ? ` · ${p.dns_name}` : ''}
                  </option>
                ))}
              </select>
              <label className="sf2-switch sf2-switch--sm" style={{ marginTop: 6 }}>
                <input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} />
                <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                <span className="sf2-switch-label">Mostrar solo libres</span>
              </label>
            </div>
          ) : mode === 'auto' ? (
            <div className="sf2-field">
              <label>Próxima IP libre</label>
              <div className="v">{nextIpLoading ? 'Calculando…' : (nextIp || 'Elige una subred')}</div>
            </div>
          ) : mode === 'map' ? (
            <div className="sf2-field">
              <label>IP seleccionada</label>
              <div className="v">{form.ip || 'Elige una IP del mapa'}</div>
            </div>
          ) : (
            <div className="sf2-field">
              <label>Dirección IP</label>
              <div className="sf2-ip-input-row">
                <input
                  value={form.ip}
                  onChange={(e) => setForm((prev) => ({ ...prev, ip: e.target.value }))}
                  placeholder="10.0.0.100"
                  disabled={guardando}
                />
                <button
                  type="button"
                  className="sf2-link"
                  onClick={pedirSiguienteLibre}
                  disabled={!form.subnet_id || buscandoLibre}
                >
                  {buscandoLibre ? 'Buscando…' : 'Libre'}
                </button>
              </div>
            </div>
          )}

          <div className="sf2-field">
            <label>Interfaz</label>
            <select
              value={form.interface_ref}
              onChange={(e) => setForm((prev) => ({ ...prev, interface_ref: e.target.value }))}
              disabled={guardando}
            >
              {opcionesInterfaz.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="sf2-field">
            <label>Tipo</label>
            <select
              value={form.assignment_type}
              onChange={(e) => setForm((prev) => ({ ...prev, assignment_type: e.target.value }))}
              disabled={guardando}
            >
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="sf2-field">
            <label>Comentario</label>
            <input
              value={form.comments}
              onChange={(e) => setForm((prev) => ({ ...prev, comments: e.target.value }))}
              placeholder="Descripción breve"
              disabled={guardando}
            />
          </div>

          <div className="sf2-field">
            <label>Prioridad</label>
            <label className="sf2-switch sf2-switch--sm">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm((prev) => ({ ...prev, is_primary: e.target.checked }))}
                disabled={guardando}
              />
              <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
              <span className="sf2-switch-label">IP primaria</span>
            </label>
          </div>
        </div>

        {mode === 'map' && (
          <div className="sf2-ip-map">
            {!form.subnet_id ? (
              <div className="sf2-empty">Selecciona primero una subred para ver el mapa.</div>
            ) : mapLoading ? (
              <div className="sf2-empty">Cargando mapa de IPs…</div>
            ) : mapData ? (
              <>
                <div className="sf2-ip-map-summary">
                  <span className="libre">■ {mapData.summary?.free ?? 0} libres</span>
                  <span className="ocupada">■ {mapData.summary?.assigned ?? 0} asignadas</span>
                  {(mapData.summary?.reserved ?? 0) > 0 && (
                    <span className="reservada">■ {mapData.summary.reserved} reservadas</span>
                  )}
                  {mapData.truncated && (
                    <span>mostrando {mapData.limit} de {mapData.summary?.usable_ips}</span>
                  )}
                </div>
                <div className="sf2-ip-map-grid">
                  {(mapData.generated_ips || []).map((entry) => {
                    const libre = entry.status === 'free' && !entry.is_gateway;
                    const seleccionada = form.ip === entry.ip;
                    const clase = entry.is_gateway
                      ? 'gw'
                      : entry.status === 'assigned'
                        ? 'asignada'
                        : entry.status === 'reserved'
                          ? 'reservada'
                          : 'libre';
                    const asignacion = entry.assignments?.[0];
                    const titulo = entry.is_gateway
                      ? `Gateway: ${entry.ip}`
                      : entry.status === 'assigned'
                        ? `Asignada: ${entry.ip}${asignacion ? ` → ${asignacion.object_name || asignacion.object_type}` : ''}`
                        : entry.status === 'reserved'
                          ? `Reservada: ${entry.ip}`
                          : `Libre: ${entry.ip} — clic para seleccionar`;
                    return (
                      <button
                        key={entry.ip}
                        type="button"
                        className={`sf2-ip-cell sf2-ip-cell--${clase}${seleccionada ? ' sf2-ip-cell--sel' : ''}`}
                        disabled={!libre}
                        title={titulo}
                        onClick={() => setForm((prev) => ({ ...prev, ip: seleccionada ? '' : entry.ip }))}
                      >
                        {entry.ip}
                      </button>
                    );
                  })}
                </div>
                <div className="sf2-ip-map-legend">Verde libre · rojo asignada · ámbar reservada · azul gateway</div>
              </>
            ) : (
              <div className="sf2-empty">No se pudo cargar el mapa de la subred.</div>
            )}
          </div>
        )}

        <div className="sf2-ip-form-actions">
          <button
            type="submit"
            className="dv-btn dv-btn-primary"
            disabled={guardando || bloqueaAlta}
          >
            {guardando ? 'Añadiendo…' : 'Añadir IP'}
          </button>
          {aviso && <span className="sf2-hint" style={{ margin: 0 }}>{aviso}</span>}
          {error && <span className="sf2-error">{error}</span>}
        </div>
      </form>

      <div className="sf2-ip-actions">
        {asignaciones.map((a) => (
          <div className="sf2-ip-action-row" key={a.id}>
            <b className="sf2-ip-addr">{a.ip}</b>
            <span>{etiquetaInterfaz(a.interface_ref, nicMap)}</span>
            {a.is_primary
              ? <span className="sf2-pill sf2-pill--ac">primaria</span>
              : <button type="button" className="sf2-link" onClick={() => marcarPrimaria(a)}>Hacer primaria</button>}
            <button type="button" className="sf2-link sf2-link--danger" onClick={() => quitar(a)}>Quitar</button>
          </div>
        ))}
      </div>
    </div>
  );
}
